/**
 * Development Environment Configuration Watcher
 * Monitors .env and dependency changes and logs them
 */

import { devLogging } from './devLoggingService';

interface EnvConfig {
  [key: string]: string | undefined;
}

class EnvWatcher {
  private currentEnv: EnvConfig = {};
  private checkInterval: number = 5000; // Check every 5 seconds
  private isWatching = false;
  private watchTimerId: number | null = null;

  constructor() {
    this.captureCurrentEnv();
  }

  /**
   * Capture current environment variables
   */
  private captureCurrentEnv() {
    const env: EnvConfig = {};

    // Vite environment variables are prefixed with VITE_
    Object.keys(import.meta.env).forEach((key) => {
      if (key.startsWith('VITE_') && key !== 'VITE_DEFINE') {
        env[key] = String(import.meta.env[key as keyof ImportMetaEnv]);
      }
    });

    this.currentEnv = env;
    return env;
  }

  /**
   * Start watching for env changes
   */
  start() {
    if (this.isWatching) {
      return;
    }

    this.isWatching = true;
    devLogging.debug('ENV_WATCHER', 'Environment watcher started', {
      checkInterval: this.checkInterval,
    });

    // Initial notification
    devLogging.info('ENV_WATCHER', 'Current environment', this.currentEnv);
  }

  /**
   * Stop watching
   */
  stop() {
    if (this.watchTimerId !== null) {
      clearInterval(this.watchTimerId);
      this.watchTimerId = null;
    }

    this.isWatching = false;
    devLogging.debug('ENV_WATCHER', 'Environment watcher stopped');
  }

  /**
   * Get current environment
   */
  getEnv(): EnvConfig {
    return { ...this.currentEnv };
  }

  /**
   * Check if specific env var exists
   */
  has(key: string): boolean {
    return key in this.currentEnv;
  }

  /**
   * Get specific env var
   */
  get(key: string): string | undefined {
    return this.currentEnv[key];
  }
}

/**
 * Development Server Health Monitor
 */
class ServerHealthMonitor {
  private backendUrl: string;
  private frontendUrl: string;
  private checkInterval: number = 30000; // Check every 30 seconds
  private isMonitoring = false;
  private monitorTimerId: number | null = null;

  constructor(backendUrl: string = 'http://localhost:5001', frontendUrl: string = 'http://localhost:3001') {
    this.backendUrl = backendUrl;
    this.frontendUrl = frontendUrl;
  }

  /**
   * Start monitoring server health
   */
  start() {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;
    devLogging.info('SERVER_HEALTH', 'Health monitoring started', {
      backend: this.backendUrl,
      frontend: this.frontendUrl,
    });

    // Perform initial check
    this.checkHealth();

    // Set interval for periodic checks
    this.monitorTimerId = window.setInterval(() => {
      this.checkHealth();
    }, this.checkInterval);
  }

  /**
   * Stop monitoring
   */
  stop() {
    if (this.monitorTimerId !== null) {
      clearInterval(this.monitorTimerId);
      this.monitorTimerId = null;
    }

    this.isMonitoring = false;
    devLogging.debug('SERVER_HEALTH', 'Health monitoring stopped');
  }

  /**
   * Check server health
   */
  private checkHealth = async () => {
    try {
      // Check backend
      const backendResponse = await fetch(`${this.backendUrl}/health`, {
        method: 'GET',
        cache: 'no-cache',
      }).catch(() => null);

      const backendStatus = backendResponse?.ok ?? false;
      if (!backendStatus) {
        devLogging.warn('SERVER_HEALTH', 'Backend health check failed', {
          url: this.backendUrl,
          status: backendResponse?.status,
        });
      }
    } catch (error) {
      devLogging.error('SERVER_HEALTH', 'Backend health check error', {
        url: this.backendUrl,
        error: String(error),
      });
    }
  };

  /**
   * Get monitoring status
   */
  isActive(): boolean {
    return this.isMonitoring;
  }

  /**
   * Update backend URL
   */
  setBackendUrl(url: string) {
    this.backendUrl = url;
    devLogging.info('SERVER_HEALTH', 'Backend URL updated', { url });
  }
}

/**
 * Build Error Detector
 * Watches for build errors and logs them
 */
class BuildErrorDetector {
  private scriptErrors: Map<string, number> = new Map();
  private maxScriptErrors = 10;

  constructor() {
    this.initializeErrorTracking();
  }

  /**
   * Initialize script error tracking
   */
  private initializeErrorTracking() {
    // Monitor script load errors
    window.addEventListener('error', (event) => {
      if (event.filename) {
        const count = (this.scriptErrors.get(event.filename) || 0) + 1;
        this.scriptErrors.set(event.filename, count);

        if (count > this.maxScriptErrors) {
          devLogging.error('BUILD_ERROR', `Too many errors from same file: ${event.filename}`, {
            count,
            error: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          });
        }
      }
    });
  }

  /**
   * Get tracked script errors
   */
  getErrorStats(): Object {
    return Object.fromEntries(this.scriptErrors);
  }

  /**
   * Clear error tracking
   */
  clearTracking() {
    this.scriptErrors.clear();
  }
}

// Export instances
export const envWatcher = new EnvWatcher();
export const serverHealthMonitor = new ServerHealthMonitor();
export const buildErrorDetector = new BuildErrorDetector();

// Make available globally for console access
declare global {
  interface Window {
    __devTools?: {
      envWatcher: EnvWatcher;
      serverHealthMonitor: ServerHealthMonitor;
      buildErrorDetector: BuildErrorDetector;
      startHealthCheck: () => void;
      stopHealthCheck: () => void;
    };
  }
}
